// v51: force rebuild for cancelled row display
import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchEmployees, exportEmployees, createEmployee, updateEmployee, deactivateEmployee, bulkUpsert, bulkDeactivateEmployees, purgeEmployee,
  fetchUsage, fetchStats, fetchAvailableMonths, fetchSettings, saveSettings, cancelUsage,
  fetchMonthlyReport, downloadMonthlyReportCsv,
  formatAmount, formatPhone, currentMonthKey,
  adminLogin, adminVerify, adminChangePassword,
  setAdminSession, clearAdminSession, isAdminSessionValid,
  type Employee, type UsageRow, type Stats, type EmployeeContribution, type MonthlyReport,
} from "@/lib/staff-api";
import { apiFetch } from "@/lib/api";

const STORES = ["명동점", "동대문점"];
type Tab = "dashboard" | "employees" | "usage" | "report" | "settings";

// ─── 로그인 게이트 ─────────────────────────────────────────────
function LoginGate({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  async function handleLogin() {
    if (!password) return;
    setLoading(true); setError("");
    const result = await adminLogin(password);
    setLoading(false);
    if (result.ok && result.token && result.expiresAt) {
      setAdminSession(result.token, result.expiresAt);
      onLogin();
    } else {
      setError(result.error ?? "비밀번호가 틀렸습니다");
      setPassword("");
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary mb-3">
            <span className="text-2xl text-primary-foreground font-bold">M</span>
          </div>
          <h1 className="text-xl font-bold text-foreground">MIMILINE</h1>
          <p className="text-sm text-muted-foreground mt-1">직원할인 관리자 로그인</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 flex flex-col gap-4 shadow-sm">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">관리자 비밀번호</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="비밀번호 입력"
                autoFocus
                className="w-full border border-border rounded-md px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
              >
                {showPw ? "숨김" : "표시"}
              </button>
            </div>
            {error && <p className="text-xs text-destructive font-medium">{error}</p>}
          </div>

          <button
            onClick={handleLogin}
            disabled={loading || !password}
            className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {loading ? "확인 중…" : "로그인"}
          </button>

        </div>

        <a href="#/" className="block text-center mt-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          ← POS 조회로 돌아가기
        </a>
      </div>
    </div>
  );
}

// ─── 대시보드 ──────────────────────────────────────────────────
function Dashboard({ defaultMonthKey }: { defaultMonthKey: string }) {
  const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthKey);
  const [availableMonths, setAvailableMonths] = useState<string[]>([defaultMonthKey]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvailableMonths().then(months => {
      const list = months.length > 0 ? months : [defaultMonthKey];
      // 현재월이 목록에 없으면 앞에 추가
      if (!list.includes(defaultMonthKey)) list.unshift(defaultMonthKey);
      setAvailableMonths(list);
    }).catch(() => {});
  }, [defaultMonthKey]);

  useEffect(() => {
    setLoading(true);
    fetchStats(selectedMonth).then(setStats).catch(() => setStats(null)).finally(() => setLoading(false));
  }, [selectedMonth]);

  if (loading) return <p className="text-sm text-muted-foreground p-4">불러오는 중…</p>;
  if (!stats) return <p className="text-sm text-destructive p-4">통계 로드 실패</p>;

  const totalLimit = stats.totalLimit ?? (stats.limit * stats.totalActive);
  const pct = totalLimit > 0 ? Math.min(100, Math.round((stats.totalPurchase / totalLimit) * 100)) : 0;

  function fmtMonth(mk: string) {
    const [y, m] = mk.split("-");
    return `${y}년 ${parseInt(m)}월${mk === defaultMonthKey ? " (이번달)" : ""}`;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* 월 선택 */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-muted-foreground whitespace-nowrap">조회 월</label>
        <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background flex-1">
          {availableMonths.map(mk => (
            <option key={mk} value={mk}>{fmtMonth(mk)}</option>
          ))}
        </select>
      </div>

      {/* KPI 타일 */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="총 판매액" value={formatAmount(stats.totalPurchase ?? 0)} sub={fmtMonth(selectedMonth)} />
        <StatCard label="총 할인액" value={formatAmount(stats.totalUsed)} sub={fmtMonth(selectedMonth)} />
        <StatCard label="재직 중 직원 수" value={`${stats.totalActive}명`} sub="활성 직원" />
        <StatCard label="구매 한도 사용률" value={`${pct}%`} sub="전체 직원 기준" accent />
      </div>

      {/* 매장별 비율 */}
      <div className="bg-card rounded-lg border border-border p-4">
        <p className="text-sm font-semibold mb-3">매장별 현황</p>
        <div className="flex flex-col gap-3">
          {STORES.map(s => {
            const found = stats.storeBreakdown.find(r => r.store === s);
            const purchase = found?.purchaseTotal ?? 0;
            const discount = found?.total ?? 0;
            const storePct = totalLimit > 0 ? Math.min(100, Math.round((purchase / totalLimit) * 100)) : 0;
            return (
              <div key={s} className="flex flex-col gap-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{s}</span>
                  <span className="text-muted-foreground">
                    판매 <strong className="text-foreground">{formatAmount(purchase)}</strong>
                    <span className="mx-1 text-border">·</span>
                    할인 <strong className="text-primary">{formatAmount(discount)}</strong>
                  </span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${storePct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub: string; accent?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-primary/30 bg-accent" : "border-border bg-card"}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
    </div>
  );
}

// ─── 직원 관리 ─────────────────────────────────────────────────
function EmployeesTab() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [storeFilter, setStoreFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", store: "명동점", department: "", position: "", monthlyLimit: "", discountRate: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", store: "명동점", department: "", position: "", monthlyLimit: "", discountRate: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeactivating, setBulkDeactivating] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [bulkConfirmAction, setBulkConfirmAction] = useState<"deactivate"|"reactivate"|"purge"|null>(null);
  const [empExporting, setEmpExporting] = useState(false);
  const [empExportErr, setEmpExportErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchEmployees(storeFilter || undefined, statusFilter || undefined)
      .then(setEmployees).catch(() => {}).finally(() => setLoading(false));
  }, [storeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedIds(new Set()); }, [statusFilter]);

  async function handleAdd() {
    if (!form.name || !form.phone) { setMsg("이름과 전화번호를 입력하세요"); return; }
    setSaving(true); setMsg("");
    const ml = form.monthlyLimit.trim() === "" ? null : parseInt(form.monthlyLimit, 10);
    const dr = form.discountRate.trim() === "" ? null : parseInt(form.discountRate, 10);
    try {
      await createEmployee({ name: form.name, phone: form.phone, store: form.store, department: form.department || undefined, position: form.position || undefined, monthlyLimit: (ml != null && !isNaN(ml)) ? ml * 10000 : null, discountRate: (dr != null && !isNaN(dr)) ? dr : null });
      setMsg("✓ 등록 완료"); setForm({ name: "", phone: "", store: "명동점", department: "", position: "", monthlyLimit: "", discountRate: "" }); setShowAdd(false); load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "등록 실패"); }
    finally { setSaving(false); }
  }

  function startEdit(emp: Employee) {
    setEditingId(emp.id);
    setEditForm({ name: emp.name, phone: emp.phone, store: emp.store || "명동점", department: emp.department || "", position: emp.position || "", monthlyLimit: emp.monthlyLimit != null ? String(emp.monthlyLimit / 10000) : "", discountRate: emp.discountRate != null ? String(emp.discountRate) : "" });
    setConfirmingId(null);
  }

  async function handleEditSave(emp: Employee) {
    if (!editForm.name || !editForm.phone) { setMsg("이름과 전화번호를 입력하세요"); return; }
    setEditSaving(true); setMsg("");
    const ml = editForm.monthlyLimit.trim() === "" ? null : parseInt(editForm.monthlyLimit, 10);
    const dr = editForm.discountRate.trim() === "" ? null : parseInt(editForm.discountRate, 10);
    try {
      await updateEmployee(emp.id, { name: editForm.name, phone: editForm.phone, store: editForm.store, department: editForm.department || undefined, position: editForm.position || undefined, monthlyLimit: (ml != null && !isNaN(ml)) ? ml * 10000 : null, discountRate: (dr != null && !isNaN(dr)) ? dr : null });
      setEditingId(null); load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "수정 실패"); }
    finally { setEditSaving(false); }
  }

  async function handleBulkAction(action: "deactivate"|"reactivate"|"purge") {
    if (selectedIds.size === 0) return;
    setBulkDeactivating(true);
    const count = selectedIds.size;
    try {
      if (action === "deactivate") {
        await bulkDeactivateEmployees(Array.from(selectedIds));
      } else if (action === "reactivate") {
        for (const id of selectedIds) await updateEmployee(id, { status: "active" });
      } else {
        for (const id of selectedIds) await purgeEmployee(id);
      }
      setSelectedIds(new Set());
      setBulkConfirmAction(null);
      const labels = { deactivate: "비활성화", reactivate: "재활성화", purge: "삭제" };
      setMsg(`✓ ${count}명 ${labels[action]} 완료`);
      load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "처리 실패"); }
    finally { setBulkDeactivating(false); }
  }

  async function handleBulkDeactivate() {
    if (selectedIds.size === 0) return;
    setBulkDeactivating(true);
    const count = selectedIds.size;
    try {
      await bulkDeactivateEmployees(Array.from(selectedIds));
      setSelectedIds(new Set());
      setBulkConfirm(false);
      setMsg(`✓ ${count}명 비활성화 완료`);
      load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "비활성화 실패"); }
    finally { setBulkDeactivating(false); }
  }

  async function handlePurge(id: number) {
    try {
      await purgeEmployee(id);
      setDeletingId(null);
      setMsg("✓ 직원 데이터 삭제 완료");
      load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "삭제 실패"); }
  }

  async function handleToggle(emp: Employee) {
    if (confirmingId !== emp.id) { setConfirmingId(emp.id); return; }
    setConfirmingId(null);
    try {
      if (emp.status === "active") await deactivateEmployee(emp.id);
      else await updateEmployee(emp.id, { status: "active" });
      load();
    } catch { setMsg("처리 실패"); }
  }

  async function handleBulk() {
    const lines = bulkText.trim().split("\n").filter(Boolean);
    if (!lines.length) { setMsg("데이터를 입력하세요"); return; }
    const parsed = lines.map(line => {
      // 형식: 이름 / 전화번호 / 부서(선택) / 직급(선택) / 매장(선택)
      const cols = line.split(/[\t,]/).map(s => s.trim());
      const [name, phone, department, position, store] = cols;
      return { name: name ?? "", phone: phone ?? "", department: department || undefined, position: position || undefined, store: store ?? "" };
    }).filter(r => r.name && r.phone);
    if (!parsed.length) { setMsg("이름, 전화번호가 포함된 데이터가 없습니다"); return; }
    setSaving(true);
    try {
      const r = await bulkUpsert(parsed);
      setMsg(`✓ 신규 ${r.results?.created ?? 0}명, 업데이트 ${r.results?.updated ?? 0}명`);
      setBulkText(""); setShowBulk(false); load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "일괄 등록 실패"); }
    finally { setSaving(false); }
  }

  async function handleEmployeesExport() {
    setEmpExporting(true);
    setEmpExportErr("");
    setMsg("");
    try {
      await exportEmployees(storeFilter || undefined, statusFilter || undefined);
    } catch (e: unknown) {
      setEmpExportErr(e instanceof Error ? e.message : "직원목록 내보내기 실패");
    } finally {
      setEmpExporting(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 flex-wrap">
        <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background">
          <option value="">전체 매장</option>
          {STORES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background">
          <option value="">전체</option>
          <option value="active">재직중</option>
          <option value="inactive">비활성</option>
        </select>
        <div className="ml-auto flex gap-2">
          {selectedIds.size > 0 && (
            bulkConfirmAction ? (
              <span className="inline-flex items-center gap-1">
                <span className="text-xs text-muted-foreground">
                  {selectedIds.size}명 {bulkConfirmAction === "deactivate" ? "비활성화" : bulkConfirmAction === "reactivate" ? "재활성화" : "영구삭제"}?
                </span>
                <button onClick={() => handleBulkAction(bulkConfirmAction)} disabled={bulkDeactivating}
                  className="text-xs px-2 py-1 rounded border border-destructive text-destructive hover:bg-destructive/10 disabled:opacity-50">
                  {bulkDeactivating ? "처리중…" : "예"}
                </button>
                <button onClick={() => setBulkConfirmAction(null)}
                  className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted/40">
                  취소
                </button>
              </span>
            ) : statusFilter === "inactive" ? (
              <span className="inline-flex items-center gap-1">
                <button onClick={() => setBulkConfirmAction("reactivate")}
                  className="text-xs px-3 py-1.5 border border-primary/40 text-primary rounded-md hover:bg-accent">
                  선택 {selectedIds.size}명 재활성화
                </button>
                <button onClick={() => setBulkConfirmAction("purge")}
                  className="text-xs px-3 py-1.5 border border-destructive text-destructive rounded-md hover:bg-destructive/10">
                  선택 {selectedIds.size}명 삭제
                </button>
              </span>
            ) : (
              <button onClick={() => setBulkConfirmAction("deactivate")}
                className="text-xs px-3 py-1.5 border border-destructive text-destructive rounded-md hover:bg-destructive/10">
                선택 {selectedIds.size}명 비활성화
              </button>
            )
          )}
          <button onClick={handleEmployeesExport} disabled={empExporting}
            className="text-xs px-3 py-1.5 border border-primary/40 text-primary rounded-md hover:bg-accent disabled:opacity-50 whitespace-nowrap">
            {empExporting ? "내보내는 중…" : "엑셀로 내보내기"}
          </button>
          <button onClick={() => { setShowBulk(!showBulk); setShowAdd(false); setMsg(""); }}
            className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-muted/40 text-muted-foreground whitespace-nowrap">
            일괄 등록
          </button>
          <button onClick={() => { setShowAdd(!showAdd); setShowBulk(false); setMsg(""); }}
            className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:opacity-90">
            + 직원 추가
          </button>
        </div>
      </div>

      {msg && <p className="text-sm font-medium text-primary">{msg}</p>}
      {empExportErr && <p className="text-xs font-medium text-destructive">{empExportErr}</p>}

      {showAdd && (
        <div className="bg-accent/30 border border-primary/20 rounded-lg p-3 flex flex-col gap-2">
          <p className="text-sm font-semibold">직원 추가</p>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="이름 *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background" />
            <input placeholder="전화번호 * (01012345678)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background font-mono" />
            <input placeholder="부서 (예: 운영팀)" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background" />
            <input placeholder="직급 (예: 팀장)" value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background" />
            <input type="number" placeholder="개별 한도 (만원, 예: 35 → 35만원, 빈칸=전역)" value={form.monthlyLimit} onChange={e => setForm(f => ({ ...f, monthlyLimit: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background" />
            <input type="number" min="1" max="100" placeholder="개인할인율 (%, 빈칸=전역)" value={form.discountRate} onChange={e => setForm(f => ({ ...f, discountRate: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background" />
            <select value={form.store} onChange={e => setForm(f => ({ ...f, store: e.target.value }))}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background col-span-2">
              {STORES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="text-xs px-3 py-1.5 border border-border rounded-md text-muted-foreground">취소</button>
            <button onClick={handleAdd} disabled={saving} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md disabled:opacity-50">
              {saving ? "저장중…" : "저장"}
            </button>
          </div>
        </div>
      )}

      {showBulk && (
        <div className="bg-accent/30 border border-primary/20 rounded-lg p-3 flex flex-col gap-2">
          <p className="text-sm font-semibold">일괄 등록</p>
          <p className="text-xs text-muted-foreground">엑셀에서 복사 붙여넣기 또는 직접 입력 (탭/쉼표 구분)<br/>형식: 이름 / 전화번호 / 부서(선택) / 직급(선택) / 매장(선택)<br/>※ 매장 생략 시 공란으로 등록</p>
          <textarea rows={6} value={bulkText} onChange={e => setBulkText(e.target.value)}
            placeholder={"홍길동\t01012345678\t운영팀\t팀장\t명동점\n이영희\t01098765432\t판매팀\t직원"}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background font-mono w-full resize-none" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowBulk(false)} className="text-xs px-3 py-1.5 border border-border rounded-md text-muted-foreground">취소</button>
            <button onClick={handleBulk} disabled={saving} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md disabled:opacity-50">
              {saving ? "등록중…" : `${bulkText.trim().split("\n").filter(Boolean).length}명 등록`}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">불러오는 중…</p>
      ) : employees.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">직원이 없습니다</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="px-3 py-2">
                  <input type="checkbox"
                    checked={employees.length > 0 && employees.every(e => selectedIds.has(e.id))}
                    onChange={e => {
                      if (e.target.checked) setSelectedIds(new Set(employees.map(emp => emp.id)));
                      else setSelectedIds(new Set());
                    }}
                    className="w-3.5 h-3.5 accent-primary cursor-pointer"
                  />
                </th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">이름</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">부서/직급</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">전화번호</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">매장</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">상태</th>
                <th className="text-right px-3 py-2 font-medium text-muted-foreground">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {employees.map(emp => (
                editingId === emp.id ? (
                  <tr key={emp.id} className="bg-accent/20">
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2">
                      <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full border border-primary/40 rounded px-2 py-1 text-sm bg-background" placeholder="이름" />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-1">
                        <input value={editForm.department} onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))}
                          className="w-full border border-border rounded px-2 py-1 text-xs bg-background" placeholder="부서" />
                        <input value={editForm.position} onChange={e => setEditForm(f => ({ ...f, position: e.target.value }))}
                          className="w-full border border-border rounded px-2 py-1 text-xs bg-background" placeholder="직급" />
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                        className="w-full border border-border rounded px-2 py-1 text-xs bg-background font-mono" placeholder="전화번호" />
                    </td>
                    <td className="px-2 py-2">
                      <select value={editForm.store} onChange={e => setEditForm(f => ({ ...f, store: e.target.value }))}
                        className="w-full border border-border rounded px-2 py-1 text-xs bg-background">
                        {STORES.map(s => <option key={s}>{s}</option>)}
                        <option value="">-</option>
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" value={editForm.monthlyLimit} onChange={e => setEditForm(f => ({ ...f, monthlyLimit: e.target.value }))}
                        className="w-full border border-border rounded px-2 py-1 text-xs bg-background" placeholder="개별한도(만원, 빈칸=전역)" />
                      <input type="number" min="1" max="100" value={editForm.discountRate} onChange={e => setEditForm(f => ({ ...f, discountRate: e.target.value }))}
                        className="w-full border border-border rounded px-2 py-1 text-xs bg-background mt-1" placeholder="개인할인율(%, 빈칸=전역)" />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className="inline-flex items-center gap-1">
                        <button onClick={() => handleEditSave(emp)} disabled={editSaving}
                          className="text-xs px-2 py-1 rounded border border-primary text-primary hover:bg-accent disabled:opacity-50">
                          {editSaving ? "…" : "저장"}
                        </button>
                        <button onClick={() => setEditingId(null)}
                          className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted/40">
                          취소
                        </button>
                      </span>
                    </td>
                  </tr>
                ) : (
                <tr key={emp.id} className={`hover:bg-muted/20 transition-colors ${emp.status !== "active" ? "opacity-50" : ""}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox"
                      checked={selectedIds.has(emp.id)}
                      onChange={e => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) next.add(emp.id); else next.delete(emp.id);
                        setSelectedIds(next);
                      }}
                      className="w-3.5 h-3.5 accent-primary cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">{emp.name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {emp.department || emp.position
                      ? <span>{[emp.department, emp.position].filter(Boolean).join(" · ")}</span>
                      : <span className="text-muted-foreground/40">-</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{formatPhone(emp.phone)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{emp.store}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      emp.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}>{emp.status === "active" ? "재직중" : "비활성"}</span>
                    {emp.monthlyLimit != null && <span className="ml-1 text-xs text-primary font-medium">{emp.monthlyLimit / 10000}만원 한도</span>}
                    {emp.discountRate != null && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">{emp.discountRate}% 할인</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="inline-flex items-center gap-1">
                      <button onClick={() => startEdit(emp)}
                        className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                        수정
                      </button>
                      {confirmingId === emp.id ? (
                        <span className="inline-flex items-center gap-1">
                          <button onClick={() => handleToggle(emp)}
                            className="text-xs px-2 py-1 rounded border border-destructive text-destructive hover:bg-destructive/10">예</button>
                          <button onClick={() => setConfirmingId(null)}
                            className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted/40">취소</button>
                        </span>
                      ) : deletingId === emp.id ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-xs text-destructive font-medium">영구삭제?</span>
                          <button onClick={() => handlePurge(emp.id)}
                            className="text-xs px-2 py-1 rounded border border-destructive text-destructive hover:bg-destructive/10">예</button>
                          <button onClick={() => setDeletingId(null)}
                            className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted/40">취소</button>
                        </span>
                      ) : (
                        <>
                          <button onClick={() => handleToggle(emp)}
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              emp.status === "active"
                                ? "border-border text-muted-foreground hover:border-destructive hover:text-destructive"
                                : "border-primary/30 text-primary hover:bg-accent"
                            }`}>
                            {emp.status === "active" ? "비활성화" : "재활성화"}
                          </button>
                          {emp.status === "inactive" && (
                            <button onClick={() => setDeletingId(emp.id)}
                              className="text-xs px-2 py-1 rounded border border-destructive/40 text-destructive/70 hover:border-destructive hover:text-destructive transition-colors">
                              삭제
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  </td>
                </tr>
                )
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">총 {employees.length}명</div>
        </div>
      )}
    </div>
  );
}

// ─── 사용 내역 ─────────────────────────────────────────────────
function UsageTab() {
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [storeFilter, setStoreFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState(currentMonthKey());
  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [cancelMsg, setCancelMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchUsage({ store: storeFilter || undefined, monthKey: monthFilter || undefined })
      .then(setRows).catch(() => {}).finally(() => setLoading(false));
  }, [storeFilter, monthFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleCancel(id: number) {
    if (cancelingId !== id) { setCancelingId(id); return; }
    setCancelingId(null);
    try {
      await cancelUsage(id);
      setCancelMsg("✓ 결제 취소 완료");
      load();
    } catch (e: unknown) { setCancelMsg(e instanceof Error ? e.message : "취소 실패"); }
  }

  const activeRows2 = rows.filter(r => r.status !== "cancelled");
  const total = activeRows2.reduce((s, r) => s + r.amount, 0);
  const contributionRows = Object.values(activeRows2.reduce((acc, r) => {
    const key = `${r.employeeId}__${r.store}`;
    const purchase = r.purchaseAmount ?? 0;
    if (!acc[key]) {
      acc[key] = {
        employeeId: r.employeeId,
        employeeName: r.employeeName,
        store: r.store,
        purchaseTotal: 0,
        discountTotal: 0,
        paymentTotal: 0,
        count: 0,
      };
    }
    acc[key].purchaseTotal += purchase;
    acc[key].discountTotal += r.amount;
    acc[key].paymentTotal += purchase > 0 ? purchase - r.amount : 0;
    acc[key].count += 1;
    return acc;
  }, {} as Record<string, EmployeeContribution>)).sort((a, b) => b.discountTotal - a.discountTotal);
  const hasPurchaseAmount = contributionRows.some(r => r.purchaseTotal > 0);

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState("");

  async function handleExport() {
    setExporting(true);
    setExportErr("");
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set("monthKey", monthFilter);
      if (storeFilter) params.set("store", storeFilter);
      const res = await apiFetch(`/discount/export?${params.toString()}`, { auth: true });
      if (!res.ok) { setExportErr("내보내기 실패 (서버 오류)"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const filename = `직원할인_${monthFilter ?? "전체"}${storeFilter ? "_" + storeFilter : ""}.csv`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setExportErr("내보내기 중 오류가 발생했습니다");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 flex-wrap items-center">
        <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background" />
        <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background">
          <option value="">전체 매장</option>
          {STORES.map(s => <option key={s}>{s}</option>)}
        </select>
        <button onClick={handleExport} disabled={exporting}
          className="ml-auto text-xs px-3 py-1.5 border border-border rounded-md hover:bg-muted/40 text-muted-foreground disabled:opacity-50 transition-colors">
          {exporting ? "⏳ 내보내는 중…" : "⬇ 엑셀 내보내기"}
        </button>
      </div>
      {exportErr && <p className="text-xs text-destructive font-medium">{exportErr}</p>}
      {cancelMsg && <p className="text-sm font-medium text-primary">{cancelMsg}</p>}
      {loading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">불러오는 중…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">사용 내역이 없습니다</p>
      ) : (
        <>
          {/* @section: usage-employee-contribution */}
          <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div>
                <p className="text-sm font-semibold">직원별 매출 기여도</p>
                <p className="text-xs text-muted-foreground mt-0.5">선택한 월/매장 기준으로 직원별 구매·할인·실결제액을 집계합니다.</p>
              </div>
              {!hasPurchaseAmount && (
                <span className="text-xs text-muted-foreground bg-muted/40 px-2 py-0.5 rounded-full whitespace-nowrap">
                  구매금액 입력 시 실결제액 표시
                </span>
              )}
            </div>
            <div className="overflow-x-auto rounded-md border border-border/70">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">직원</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">매장</th>
                    {hasPurchaseAmount && <th className="text-right px-3 py-2 font-semibold text-muted-foreground">구매금액</th>}
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">할인금액</th>
                    {hasPurchaseAmount && <th className="text-right px-3 py-2 font-semibold text-muted-foreground">실결제액</th>}
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">건수</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {contributionRows.map((c, i) => (
                    <tr key={`${c.employeeId}__${c.store}`} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                      <td className="px-3 py-2 font-medium">{c.employeeName}</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.store}</td>
                      {hasPurchaseAmount && <td className="px-3 py-2 text-right">{c.purchaseTotal > 0 ? formatAmount(c.purchaseTotal) : <span className="text-muted-foreground/50">—</span>}</td>}
                      <td className="px-3 py-2 text-right text-primary font-semibold">{formatAmount(c.discountTotal)}</td>
                      {hasPurchaseAmount && <td className="px-3 py-2 text-right font-semibold">{c.paymentTotal > 0 ? formatAmount(c.paymentTotal) : <span className="text-muted-foreground/50">—</span>}</td>}
                      <td className="px-3 py-2 text-right text-muted-foreground">{c.count}건</td>
                    </tr>
                  ))}
                </tbody>
                {contributionRows.length > 1 && (
                  <tfoot className="border-t border-border bg-muted/20">
                    <tr className="font-bold">
                      <td className="px-3 py-2" colSpan={2}>합계</td>
                      {hasPurchaseAmount && <td className="px-3 py-2 text-right">{formatAmount(contributionRows.reduce((s, c) => s + c.purchaseTotal, 0))}</td>}
                      <td className="px-3 py-2 text-right text-primary">{formatAmount(contributionRows.reduce((s, c) => s + c.discountTotal, 0))}</td>
                      {hasPurchaseAmount && <td className="px-3 py-2 text-right">{formatAmount(contributionRows.reduce((s, c) => s + c.paymentTotal, 0))}</td>}
                      <td className="px-3 py-2 text-right text-muted-foreground">{contributionRows.reduce((s, c) => s + c.count, 0)}건</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* @section: usage-history-table */}
          {(() => {
            const activeRows = rows.filter(r => r.status !== "cancelled");
            const totalPurchase = activeRows.reduce((s, r) => s + (r.purchaseAmount ?? 0), 0);
            const totalDiscount = activeRows.reduce((s, r) => s + r.amount, 0);
            const totalPayment = totalPurchase - totalDiscount;
            return (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">일시</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">직원</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">매장</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">구매금액</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">할인금액</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">실결제액</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">취소</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map(r => {
                      const isCancelled = r.status === "cancelled";
                      const purchase = r.purchaseAmount ?? 0;
                      const payment = purchase > 0 ? purchase - r.amount : 0;
                      return (
                        <tr key={r.id} className={`transition-colors ${isCancelled ? "bg-muted/40 opacity-60" : "hover:bg-muted/20"}`}>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                            <span className={isCancelled ? "line-through" : ""}>{r.createdAt.slice(0, 16)}</span>
                            {isCancelled && <span className="ml-1 text-[10px] text-destructive font-medium bg-destructive/10 px-1 py-0.5 rounded">취소됨</span>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`font-medium ${isCancelled ? "line-through text-muted-foreground" : ""}`}>{r.employeeName}</span>
                            <span className="ml-1 text-xs text-muted-foreground">{formatPhone(r.employeePhone)}</span>
                          </td>
                          <td className={`px-3 py-2 text-muted-foreground ${isCancelled ? "line-through" : ""}`}>{r.store}</td>
                          <td className={`px-3 py-2 text-right text-muted-foreground ${isCancelled ? "line-through" : ""}`}>{purchase > 0 ? formatAmount(purchase) : <span className="text-muted-foreground/40">—</span>}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${isCancelled ? "line-through text-muted-foreground" : "text-primary"}`}>− {formatAmount(r.amount)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${isCancelled ? "line-through text-muted-foreground" : ""}`}>{payment > 0 ? formatAmount(payment) : <span className="text-muted-foreground/40">—</span>}</td>
                          <td className="px-3 py-2 text-right">
                            {isCancelled ? (
                              <span className="text-xs text-muted-foreground/60">—</span>
                            ) : cancelingId === r.id ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">취소?</span>
                                <button onClick={() => handleCancel(r.id)}
                                  className="text-xs px-2 py-1 rounded border border-destructive text-destructive hover:bg-destructive/10">
                                  예
                                </button>
                                <button onClick={() => setCancelingId(null)}
                                  className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted/40">
                                  아니오
                                </button>
                              </span>
                            ) : (
                              <button onClick={() => handleCancel(r.id)}
                                className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-colors">
                                취소
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-border bg-muted/30">
                    <tr className="font-bold">
                      <td colSpan={3} className="px-3 py-2 text-sm">합계 ({activeRows.length}건)</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{formatAmount(totalPurchase)}</td>
                      <td className="px-3 py-2 text-right text-primary">− {formatAmount(totalDiscount)}</td>
                      <td className="px-3 py-2 text-right">{formatAmount(totalPayment)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// ─── 월간 정산 보고서 ───────────────────────────────────────────
function MonthlyReportTab() {
  const curMonth = currentMonthKey();
  const [selectedMonth, setSelectedMonth] = useState(curMonth);
  const [availableMonths, setAvailableMonths] = useState<string[]>([curMonth]);
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchAvailableMonths().then(months => {
      const list = months.length > 0 ? months : [curMonth];
      if (!list.includes(curMonth)) list.unshift(curMonth);
      setAvailableMonths(list);
    }).catch(() => {});
  }, [curMonth]);

  useEffect(() => {
    setLoading(true); setErr("");
    fetchMonthlyReport(selectedMonth)
      .then(setReport)
      .catch(e => { setErr(e instanceof Error ? e.message : "보고서 로드 실패"); setReport(null); })
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  async function handleExport() {
    setExporting(true); setErr("");
    try { await downloadMonthlyReportCsv(selectedMonth); }
    catch (e) { setErr(e instanceof Error ? e.message : "내보내기 실패"); }
    finally { setExporting(false); }
  }

  function fmtMonth(mk: string) {
    const [y, m] = mk.split("-");
    return `${y}년 ${parseInt(m)}월${mk === curMonth ? " (이번달)" : ""}`;
  }

  const s = report?.summary;

  return (
    <div className="flex flex-col gap-4">
      {/* 헤더 컨트롤 */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1">
          <label className="text-xs font-semibold text-muted-foreground whitespace-nowrap">정산 월</label>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background flex-1">
            {availableMonths.map(mk => (
              <option key={mk} value={mk}>{fmtMonth(mk)}</option>
            ))}
          </select>
        </div>
        <button onClick={handleExport} disabled={exporting || !report}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap">
          {exporting ? "⏳ 내보내는 중…" : "⬇ CSV 다운로드"}
        </button>
      </div>
      {err && <p className="text-xs text-destructive font-medium">{err}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">불러오는 중…</p>
      ) : !report ? null : (
        <>
          {/* 요약 KPI */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-sm font-bold text-primary mb-3">
              📊 {fmtMonth(report.monthKey)} 정산 요약
              <span className="ml-2 text-xs font-normal text-muted-foreground">할인율 {report.discountRate}% · 재직 직원 {s?.totalActive ?? 0}명</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">총 구매금액</p>
                <p className="text-lg font-bold text-foreground mt-0.5">{formatAmount(s?.grandPurchase ?? 0)}</p>
              </div>
              <div className="bg-white rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">총 할인금액</p>
                <p className="text-lg font-bold text-primary mt-0.5">{formatAmount(s?.grandDiscount ?? 0)}</p>
              </div>
              <div className="bg-white rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">총 실결제금액</p>
                <p className="text-lg font-bold text-foreground mt-0.5">{formatAmount(s?.grandPayment ?? 0)}</p>
              </div>
              <div className="bg-white rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">전체 잔여 한도</p>
                <p className="text-lg font-bold text-green-700 mt-0.5">{formatAmount(s?.grandRemaining ?? 0)}</p>
              </div>
            </div>
          </div>

          {/* 직원별 상세 테이블 */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="bg-muted/40 px-4 py-2.5 flex items-center justify-between border-b border-border">
              <p className="text-sm font-semibold">직원별 잔여 한도 현황</p>
              <p className="text-xs text-muted-foreground">총 {report.employees.length}명</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">직원</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">매장</th>
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">구매금액</th>
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">할인금액</th>
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">실결제액</th>
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">잔여한도</th>
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">사용률</th>
                    <th className="text-right px-3 py-2 font-semibold text-muted-foreground">건수</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.employees.map((e, i) => {
                    const overHalf = e.usagePct >= 50;
                    const full = e.usagePct >= 100;
                    return (
                      <tr key={e.employeeId} className={i % 2 === 0 ? "" : "bg-muted/10"}>
                        <td className="px-3 py-2">
                          <p className="font-medium text-foreground">{e.name}</p>
                          {(e.department || e.position) && (
                            <p className="text-muted-foreground/70">{[e.department, e.position].filter(Boolean).join(" · ")}</p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{e.store}</td>
                        <td className="px-3 py-2 text-right">
                          {e.totalPurchase > 0 ? formatAmount(e.totalPurchase) : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-primary font-semibold">
                          {e.totalDiscount > 0 ? `− ${formatAmount(e.totalDiscount)}` : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {e.totalPayment > 0 ? formatAmount(e.totalPayment) : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${full ? "text-destructive" : overHalf ? "text-amber-600" : "text-green-700"}`}>
                          {formatAmount(e.remaining)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${full ? "bg-destructive" : overHalf ? "bg-amber-500" : "bg-green-500"}`}
                                style={{ width: `${Math.min(100, e.usagePct)}%` }} />
                            </div>
                            <span className={full ? "text-destructive font-bold" : overHalf ? "text-amber-600" : "text-muted-foreground"}>
                              {e.usagePct}%
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{e.count}건</td>
                      </tr>
                    );
                  })}
                </tbody>
                {report.employees.length > 0 && (
                  <tfoot className="border-t-2 border-border bg-muted/30 font-bold">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>합계 ({report.employees.length}명)</td>
                      <td className="px-3 py-2 text-right">{formatAmount(s?.grandPurchase ?? 0)}</td>
                      <td className="px-3 py-2 text-right text-primary">− {formatAmount(s?.grandDiscount ?? 0)}</td>
                      <td className="px-3 py-2 text-right">{formatAmount(s?.grandPayment ?? 0)}</td>
                      <td className="px-3 py-2 text-right text-green-700">{formatAmount(s?.grandRemaining ?? 0)}</td>
                      <td />
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {report.employees.reduce((acc, e) => acc + e.count, 0)}건
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {report.employees.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">이번 달 사용 내역이 없습니다</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── 설정 ──────────────────────────────────────────────────────
function SettingsTab({ onLogout }: { onLogout: () => void }) {
  const [limit, setLimit] = useState("300000");
  const [rate, setRate] = useState("30");
  const [pin, setPin] = useState("");
  const [resetDay, setResetDay] = useState("1");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [supportPw, setSupportPw] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    fetchSettings().then(s => {
      setLimit(String(s.monthly_limit));
      setRate(String(s.discount_rate));
      setPin(s.discount_pin ?? "");
      setResetDay(String(s.reset_day ?? 1));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    const ml = parseInt(limit, 10);
    const dr = parseInt(rate, 10);
    const rd = parseInt(resetDay, 10);
    if (isNaN(ml) || ml <= 0) { setMsg("유효한 한도를 입력하세요"); return; }
    if (isNaN(dr) || dr < 1 || dr > 100) { setMsg("할인율은 1~100 사이"); return; }
    if (isNaN(rd) || rd < 1 || rd > 28) { setMsg("리셋일은 1~28 사이"); return; }
    setSaving(true); setMsg("");
    try {
      await saveSettings({ monthly_limit: ml, discount_rate: dr, discount_pin: pin, reset_day: rd });
      setMsg("✓ 설정 저장 완료");
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : "저장 실패"); }
    finally { setSaving(false); }
  }

  async function handleChangePw() {
    if (supportPw !== "support@mimiline") { setPwMsg("지원 비밀번호가 올바르지 않습니다"); return; }
    if (!newPw || newPw.length < 4) { setPwMsg("비밀번호는 4자 이상이어야 합니다"); return; }
    if (newPw !== confirmPw) { setPwMsg("비밀번호가 일치하지 않습니다"); return; }
    setPwSaving(true); setPwMsg("");
    const result = await adminChangePassword(newPw);
    setPwSaving(false);
    if (result.ok && result.token && result.expiresAt) {
      setAdminSession(result.token, result.expiresAt);
      setPwMsg("✓ 비밀번호 변경 완료");
      setNewPw(""); setConfirmPw(""); setSupportPw("");
    } else {
      setPwMsg(result.error ?? "변경 실패");
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground p-4">불러오는 중…</p>;

  return (
    <div className="flex flex-col gap-6 max-w-sm">
      {/* 할인 설정 */}
      <div className="flex flex-col gap-3">
        <p className="text-sm font-bold border-b border-border pb-2">할인 정책 설정</p>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">월 구매 한도 (원, 판매액 기준)</label>
          <input type="number" value={limit} onChange={e => setLimit(e.target.value)}
            className="border border-border rounded-md px-3 py-2 bg-background text-sm" />
          <p className="text-xs text-muted-foreground">직원 1인당 한달에 구매할 수 있는 금액 상한. 현재: {formatAmount(parseInt(limit) || 0)}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">직원 할인율 (%)</label>
          <input type="number" value={rate} min="1" max="100" onChange={e => setRate(e.target.value)}
            className="border border-border rounded-md px-3 py-2 bg-background text-sm" />
          <p className="text-xs text-muted-foreground">정가 대비 {rate}% 할인</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">한도 리셋일 (매월 몇 일)</label>
          <input type="number" value={resetDay} min="1" max="28" onChange={e => setResetDay(e.target.value)}
            className="border border-border rounded-md px-3 py-2 bg-background text-sm" />
          <p className="text-xs text-muted-foreground">매월 {resetDay}일에 구매 한도 초기화. 1~28 사이 입력 (기본: 1일)</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">결제 인증 비밀번호</label>
          <input type="text" value={pin} onChange={e => setPin(e.target.value)} placeholder="예: 79 (비워두면 인증 없음)"
            className="border border-border rounded-md px-3 py-2 bg-background text-sm font-mono tracking-widest" />
          <p className="text-xs text-muted-foreground">POS에서 할인 시 직원이 입력해야 하는 공용 비밀번호. 비워두면 2차 인증 없이 할인됩니다.</p>
        </div>
        {msg && <p className="text-sm font-medium text-primary">{msg}</p>}
        <button onClick={handleSave} disabled={saving}
          className="py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-md disabled:opacity-50 hover:opacity-90">
          {saving ? "저장중…" : "설정 저장"}
        </button>
      </div>

      {/* 비밀번호 변경 */}
      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <p className="text-sm font-bold">관리자 비밀번호 변경</p>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">2차 비밀번호 <span className="text-xs text-muted-foreground font-normal">(관리자 비밀번호 변경 승인용)</span></label>
          <input
            type="password"
            value={supportPw}
            onChange={e => { setSupportPw(e.target.value); setPwMsg(""); }}
            placeholder="2차 비밀번호 입력"
            className="border border-border rounded-md px-3 py-2 bg-background text-sm"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">새 비밀번호</label>
          <div className="relative">
            <input
              type={showNewPw ? "text" : "password"}
              value={newPw}
              onChange={e => { setNewPw(e.target.value); setPwMsg(""); }}
              placeholder="4자 이상"
              className="w-full border border-border rounded-md px-3 py-2 bg-background text-sm pr-10"
            />
            <button type="button" onClick={() => setShowNewPw(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
              {showNewPw ? "숨김" : "표시"}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">새 비밀번호 확인</label>
          <input
            type="password"
            value={confirmPw}
            onChange={e => { setConfirmPw(e.target.value); setPwMsg(""); }}
            placeholder="비밀번호 재입력"
            className="border border-border rounded-md px-3 py-2 bg-background text-sm"
          />
        </div>
        {pwMsg && <p className={`text-sm font-medium ${pwMsg.startsWith("✓") ? "text-primary" : "text-destructive"}`}>{pwMsg}</p>}
        <button onClick={handleChangePw} disabled={pwSaving || !newPw || !confirmPw}
          className="py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-md disabled:opacity-50 hover:opacity-90">
          {pwSaving ? "변경중…" : "비밀번호 변경"}
        </button>
      </div>

      {/* 로그아웃 */}
      <div className="border-t border-border pt-4">
        <button onClick={onLogout}
          className="w-full py-2.5 border border-border text-sm text-muted-foreground rounded-md hover:bg-muted/40 transition-colors">
          로그아웃
        </button>
      </div>
    </div>
  );
}

// ─── 메인 관리자 페이지 ─────────────────────────────────────────
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30분
const WARN_BEFORE_MS = 5 * 60 * 1000;   // 만료 5분 전 경고

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("dashboard");
  const [idleWarning, setIdleWarning] = useState(false);
  const monthKey = currentMonthKey();
  const lastActivityRef = useRef<number>(Date.now());

  // 초기 인증 상태 확인
  useEffect(() => {
    if (!isAdminSessionValid()) {
      setIsAuthenticated(false);
      return;
    }
    adminVerify().then(valid => setIsAuthenticated(valid)).catch(() => setIsAuthenticated(false));
  }, []);

  function handleLogout() {
    clearAdminSession();
    setIsAuthenticated(false);
    setIdleWarning(false);
  }

  // ── 비활동 자동 로그아웃 ─────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const resetActivity = () => { lastActivityRef.current = Date.now(); setIdleWarning(false); };
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll", "click"];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        clearAdminSession();
        setIsAuthenticated(false);
        setIdleWarning(false);
      } else if (idle >= IDLE_TIMEOUT_MS - WARN_BEFORE_MS) {
        setIdleWarning(true);
      } else {
        setIdleWarning(false);
      }
    }, 30_000); // 30초마다 체크

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  // 로딩 중
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-muted-foreground">확인 중…</p>
      </div>
    );
  }

  // 비인증 → 로그인 게이트
  if (!isAuthenticated) {
    return <LoginGate onLogin={() => setIsAuthenticated(true)} />;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "dashboard", label: "대시보드" },
    { key: "employees", label: "직원관리" },
    { key: "usage", label: "사용내역" },
    { key: "report", label: "월간보고서" },
    { key: "settings", label: "설정" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* 비활동 경고 배너 */}
      {idleWarning && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-2 sticky top-0 z-20">
          <p className="text-xs text-amber-800 font-medium">⏱ 곧 자동 로그아웃됩니다. 계속 사용하려면 화면을 클릭하세요.</p>
          <button onClick={() => { lastActivityRef.current = Date.now(); setIdleWarning(false); }}
            className="text-xs text-amber-700 underline hover:text-amber-900">
            계속 유지
          </button>
        </div>
      )}
      <header className="border-b border-border bg-primary text-primary-foreground sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold tracking-tight">MIMILINE</span>
            <span className="text-xs opacity-70 font-medium">직원할인 관리자</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#/" className="text-sm font-medium px-3 py-1.5 rounded-md bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors">← POS</a>
            <button onClick={handleLogout} className="text-sm font-medium px-3 py-1.5 rounded-md bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors">로그아웃</button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 flex gap-0 border-t border-primary-foreground/20">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-primary-foreground text-primary-foreground"
                  : "border-transparent text-primary-foreground/60 hover:text-primary-foreground/90"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-5">
        {tab === "dashboard" && <Dashboard defaultMonthKey={monthKey} />}
        {tab === "employees" && <EmployeesTab />}
        {tab === "usage" && <UsageTab />}
        {tab === "report" && <MonthlyReportTab />}
        {tab === "settings" && <SettingsTab onLogout={handleLogout} />}
      </main>
    </div>
  );
}
